import { z } from 'zod';

const VARIETY_CODE_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const varietySchema = z.object({
  id: z.string(),
  cropId: z.string(),
  cropCode: z.string(),
  cropName: z.string(),
  code: z.string(),
  name: z.string(),
  imageStorageKey: z.string().nullable(),
  thumbnailStorageKey: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Variety = z.infer<typeof varietySchema>;

const varietyTranslationSchema = z.object({
  id: z.string(),
  localeId: z.string(),
  localeCode: z.string(),
  name: z.string(),
});
export const varietyDetailSchema = varietySchema.extend({
  translations: z.array(varietyTranslationSchema),
});
export type VarietyDetail = z.infer<typeof varietyDetailSchema>;

export const varietyOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  cropId: z.string(),
});
export type VarietyOption = z.infer<typeof varietyOptionSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Form schema
// ────────────────────────────────────────────────────────────────────────────

export const varietyFormTranslationSchema = z.object({
  localeCode: z.string().min(1),
  localeName: z.string(),
  name: z.string().max(200),
});
export type VarietyFormTranslation = z.infer<typeof varietyFormTranslationSchema>;

export const varietyFormSchema = z.object({
  // Stored as the option-id string (BigInt-safe). Required on create; ignored on update.
  cropId: z.string().min(1, 'Crop is required'),
  code: z
    .string()
    .min(1, 'Code is required')
    .max(100)
    .regex(VARIETY_CODE_REGEX, 'Lowercase alphanumerics joined by single hyphens'),
  name: z.string().min(1, 'Name is required').max(200),
  imageStorageKey: z.string().max(500).nullable(),
  thumbnailStorageKey: z.string().max(500).nullable(),
  isActive: z.boolean(),
  translations: z.array(varietyFormTranslationSchema),
});
export type VarietyFormValues = z.infer<typeof varietyFormSchema>;

// ────────────────────────────────────────────────────────────────────────────
// List filters
// ────────────────────────────────────────────────────────────────────────────

export const VARIETY_SORT_KEYS = ['code', 'name', 'crop', 'updated'] as const;
export type VarietySortKey = (typeof VARIETY_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface ListVarietiesParams {
  page: number;
  pageSize: number;
  searchText?: string;
  cropId?: number;
  isActive?: 'true' | 'false';
  sortBy?: VarietySortKey;
  sortDir?: SortDirection;
}
